/**
 * Edge Commerce Protocol Engine - Theme Hook
 * Comprehensive theme management with dark/light mode support
 */

import React, { useState, useEffect, useCallback } from 'react';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeConfig {
  theme: Theme;
  systemTheme: 'light' | 'dark';
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const THEME_STORAGE_KEY = 'edge-commerce-theme';
const THEME_ATTRIBUTE = 'data-theme';

export function useTheme(): ThemeConfig {
  const [theme, setThemeState] = useState<Theme>('system');
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');

  // Get system theme preference
  const getSystemTheme = useCallback((): 'light' | 'dark' => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }, []);

  // Get effective theme (what's actually applied)
  const getEffectiveTheme = useCallback((currentTheme: Theme): 'light' | 'dark' => {
    if (currentTheme === 'system') {
      return getSystemTheme();
    }
    return currentTheme;
  }, [getSystemTheme]);

  // Apply theme to DOM
  const applyTheme = useCallback((themeValue: 'light' | 'dark') => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute(THEME_ATTRIBUTE, themeValue);
    }
  }, []);

  // Load saved theme on mount
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme;
      if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
        setThemeState(savedTheme);
      }
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
    }
  }, []);

  // Monitor system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        setSystemTheme(e.matches ? 'dark' : 'light');
      };

      // Initial value
      setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

      // Listen for changes
      mediaQuery.addEventListener('change', handleChange);
      
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, []);

  // Apply theme whenever theme or system theme changes
  useEffect(() => {
    const effective = getEffectiveTheme(theme);
    applyTheme(effective);
  }, [theme, systemTheme, getEffectiveTheme, applyTheme]);

  // Set theme function
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, []);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    const effective = getEffectiveTheme(theme);
    setTheme(effective === 'light' ? 'dark' : 'light');
  }, [theme, getEffectiveTheme, setTheme]);

  const effectiveTheme = getEffectiveTheme(theme);

  return {
    theme,
    systemTheme,
    effectiveTheme,
    setTheme,
    toggleTheme,
  };
}

// Theme utilities for server-side rendering
export function getThemeScript(): string {
  const storageKey = THEME_STORAGE_KEY;
  const themeAttr = THEME_ATTRIBUTE;
  
  return `(function() {
    const THEME_STORAGE_KEY = '${storageKey}';
    const THEME_ATTRIBUTE = '${themeAttr}';
    
    function getSystemTheme() {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    
    function getStoredTheme() {
      try {
        return localStorage.getItem(THEME_STORAGE_KEY);
      } catch {
        return null;
      }
    }
    
    function getEffectiveTheme() {
      const stored = getStoredTheme();
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
      return getSystemTheme();
    }
    
    const theme = getEffectiveTheme();
    document.documentElement.setAttribute(THEME_ATTRIBUTE, theme);
  })();`;
}

// Theme provider component
interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
  const { setTheme } = useTheme();

  // Initialize theme if not set
  useEffect(() => {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (!storedTheme) {
      setTheme(defaultTheme);
    }
  }, [defaultTheme, setTheme]);

  return React.createElement(React.Fragment, null, children);
}
